/**
 * PlantillaEncuestaPage - Administración de Plantilla de Encuesta
 * Solo accesible por superadmin.
 * CRUD completo de preguntas y opciones de respuesta.
 */
import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  ListOrdered,
  HelpCircle,
  Settings2,
} from 'lucide-react';
import { encuestasAPI } from '../../api';
import useAuthStore from '../../store/authStore';
import {
  Button,
  Card,
  Loading,
  Alert,
  Modal,
} from '../../components/common';

const SECCIONES = [
  { value: 'I', label: 'I - Privacidad y Datos Personales' },
  { value: 'II', label: 'II - Algoritmos y Equidad' },
  { value: 'III', label: 'III - Impacto Social' },
  { value: 'IV', label: 'IV - Gobernanza y Cultura' },
];

const TIPOS_PREGUNTA = [
  { value: 'ESCALA', label: 'Escala (Sí / Parcialmente / No)' },
  { value: 'OPCION_MULTIPLE', label: 'Opción Múltiple' },
  { value: 'ABIERTA', label: 'Respuesta Abierta' },
];

const NOMBRES_SECCION = {
  I: 'Privacidad y Datos Personales',
  II: 'Algoritmos y Equidad',
  III: 'Impacto Social',
  IV: 'Gobernanza y Cultura',
};

const emptyPregunta = {
  numero_pregunta: '',
  seccion: 'I',
  nombre_seccion: 'Privacidad y Datos Personales',
  enunciado: '',
  tipo_pregunta: 'ESCALA',
};

const emptyOpcion = {
  codigo_opcion: '',
  texto_opcion: '',
  valor_ponderado: 0,
  orden: 1,
};

const PlantillaEncuestaPage = () => {
  const { user } = useAuthStore();

  const [preguntas, setPreguntas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal de pregunta
  const [showPreguntaModal, setShowPreguntaModal] = useState(false);
  const [editingPregunta, setEditingPregunta] = useState(null);
  const [preguntaForm, setPreguntaForm] = useState({ ...emptyPregunta });

  // Modal de opciones
  const [showOpcionesModal, setShowOpcionesModal] = useState(false);
  const [selectedPregunta, setSelectedPregunta] = useState(null);
  const [opcionForm, setOpcionForm] = useState({ ...emptyOpcion });
  const [editingOpcion, setEditingOpcion] = useState(null);

  // Modal de confirmación de eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Expandir/colapsar secciones
  const [expandedSections, setExpandedSections] = useState({ I: true, II: true, III: true, IV: true });

  const loadPreguntas = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await encuestasAPI.getPlantilla();
      setPreguntas(response.data || []);
    } catch (err) {
      console.error('Error al cargar plantilla:', err);
      setError(err.message || 'Error al cargar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreguntas();
  }, []);

  const showSuccessMsg = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  // === PREGUNTA HANDLERS ===

  const handleOpenCreatePregunta = () => {
    const maxNumero = preguntas.length > 0
      ? Math.max(...preguntas.map(p => p.numero_pregunta))
      : 0;
    setPreguntaForm({ ...emptyPregunta, numero_pregunta: maxNumero + 1 });
    setEditingPregunta(null);
    setShowPreguntaModal(true);
  };

  const handleOpenEditPregunta = (pregunta) => {
    setPreguntaForm({
      numero_pregunta: pregunta.numero_pregunta,
      seccion: pregunta.seccion,
      nombre_seccion: pregunta.nombre_seccion,
      enunciado: pregunta.enunciado,
      tipo_pregunta: pregunta.tipo_pregunta,
    });
    setEditingPregunta(pregunta);
    setShowPreguntaModal(true);
  };

  const handleSavePregunta = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const payload = {
        ...preguntaForm,
        numero_pregunta: parseInt(preguntaForm.numero_pregunta, 10),
        nombre_seccion: NOMBRES_SECCION[preguntaForm.seccion] || preguntaForm.nombre_seccion,
      };

      if (editingPregunta) {
        await encuestasAPI.updatePreguntaPlantilla(editingPregunta.id_pregunta, payload);
        showSuccessMsg('Pregunta actualizada correctamente');
      } else {
        await encuestasAPI.createPreguntaPlantilla(payload);
        showSuccessMsg('Pregunta creada correctamente');
      }
      setShowPreguntaModal(false);
      loadPreguntas();
    } catch (err) {
      setError(err.message || 'Error al guardar la pregunta');
    }
  };

  const handleDeletePregunta = (pregunta) => {
    setDeleteTarget({ type: 'pregunta', item: pregunta });
    setShowDeleteModal(true);
  };

  // === OPCION HANDLERS ===

  const handleOpenOpciones = (pregunta) => {
    setSelectedPregunta(pregunta);
    setOpcionForm({ ...emptyOpcion, orden: (pregunta.opciones?.length || 0) + 1 });
    setEditingOpcion(null);
    setShowOpcionesModal(true);
  };

  const handleSaveOpcion = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const payload = {
        ...opcionForm,
        valor_ponderado: parseInt(opcionForm.valor_ponderado, 10) || 0,
        orden: parseInt(opcionForm.orden, 10),
      };

      if (editingOpcion) {
        await encuestasAPI.updateOpcion(editingOpcion.id_opcion, payload);
        showSuccessMsg('Opción actualizada');
      } else {
        await encuestasAPI.createOpcion(selectedPregunta.id_pregunta, payload);
        showSuccessMsg('Opción agregada');
      }
      setEditingOpcion(null);
      setOpcionForm({ ...emptyOpcion, orden: (selectedPregunta.opciones?.length || 0) + 2 });
      loadPreguntas();
      // Actualizar selectedPregunta
      const updated = (await encuestasAPI.getPlantilla()).data || [];
      const refreshed = updated.find(p => p.id_pregunta === selectedPregunta.id_pregunta);
      if (refreshed) setSelectedPregunta(refreshed);
    } catch (err) {
      setError(err.message || 'Error al guardar la opción');
    }
  };

  const handleEditOpcion = (opcion) => {
    setOpcionForm({
      codigo_opcion: opcion.codigo_opcion,
      texto_opcion: opcion.texto_opcion,
      valor_ponderado: opcion.valor_ponderado || 0,
      orden: opcion.orden,
    });
    setEditingOpcion(opcion);
  };

  const handleDeleteOpcion = (opcion) => {
    setDeleteTarget({ type: 'opcion', item: opcion });
    setShowDeleteModal(true);
  };

  // === CONFIRMAR ELIMINACIÓN ===

  const confirmDelete = async () => {
    try {
      setError(null);
      if (deleteTarget.type === 'pregunta') {
        await encuestasAPI.deletePreguntaPlantilla(deleteTarget.item.id_pregunta);
        showSuccessMsg('Pregunta eliminada');
      } else if (deleteTarget.type === 'opcion') {
        await encuestasAPI.deleteOpcion(deleteTarget.item.id_opcion);
        showSuccessMsg('Opción eliminada');
        // Refrescar opciones si el modal de opciones está abierto
        if (selectedPregunta) {
          const updated = (await encuestasAPI.getPlantilla()).data || [];
          const refreshed = updated.find(p => p.id_pregunta === selectedPregunta.id_pregunta);
          if (refreshed) setSelectedPregunta(refreshed);
        }
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadPreguntas();
    } catch (err) {
      setError(err.message || 'Error al eliminar');
    }
  };

  const toggleSection = (seccion) => {
    setExpandedSections(prev => ({ ...prev, [seccion]: !prev[seccion] }));
  };

  // Agrupar preguntas por sección
  const preguntasPorSeccion = SECCIONES.map(s => ({
    ...s,
    preguntas: preguntas.filter(p => p.seccion === s.value),
  }));

  if (loading) {
    return <Loading fullScreen text="Cargando plantilla de encuesta..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plantilla de Encuesta</h1>
          <p className="mt-1 text-sm text-gray-500">
            Administra las preguntas y opciones de respuesta de la encuesta ética
          </p>
        </div>
        <Button variant="primary" onClick={handleOpenCreatePregunta} icon={Plus}>
          Nueva Pregunta
        </Button>
      </div>

      {/* Alertas */}
      {error && (
        <Alert type="error" title="Error" message={error} onClose={() => setError(null)} />
      )}
      {success && (
        <Alert type="success" title="Éxito" message={success} onClose={() => setSuccess(null)} />
      )}

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <HelpCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Preguntas</p>
              <p className="text-2xl font-bold">{preguntas.length}</p>
            </div>
          </div>
        </Card>
        {SECCIONES.map(s => (
          <Card key={s.value} className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-lg">
                <ListOrdered className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Sección {s.value}</p>
                <p className="text-lg font-bold">
                  {preguntas.filter(p => p.seccion === s.value).length} preg.
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Preguntas por sección */}
      {preguntasPorSeccion.map(seccion => (
        <Card key={seccion.value}>
          <button
            onClick={() => toggleSection(seccion.value)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg font-semibold text-gray-900">
                {seccion.label}
              </span>
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {seccion.preguntas.length} preguntas
              </span>
            </div>
            {expandedSections[seccion.value] ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {expandedSections[seccion.value] && (
            <div className="border-t divide-y">
              {seccion.preguntas.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  No hay preguntas en esta sección
                </div>
              ) : (
                seccion.preguntas.map(pregunta => (
                  <div key={pregunta.id_pregunta} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                            {pregunta.numero_pregunta}
                          </span>
                          <span className="text-xs text-gray-400 uppercase">
                            {pregunta.tipo_pregunta}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900">{pregunta.enunciado}</p>
                        {/* Opciones inline */}
                        {pregunta.opciones && pregunta.opciones.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {pregunta.opciones.map(op => (
                              <span
                                key={op.id_opcion}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-md"
                              >
                                <span className="font-medium">{op.codigo_opcion}:</span>
                                {op.texto_opcion}
                                {op.valor_ponderado != null && (
                                  <span className="text-gray-400">({op.valor_ponderado}pts)</span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleOpenOpciones(pregunta)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Gestionar opciones"
                        >
                          <Settings2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEditPregunta(pregunta)}
                          className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                          title="Editar pregunta"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePregunta(pregunta)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar pregunta"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>
      ))}

      {/* ===== MODAL: Crear/Editar Pregunta ===== */}
      <Modal
        isOpen={showPreguntaModal}
        onClose={() => setShowPreguntaModal(false)}
        title={editingPregunta ? 'Editar Pregunta' : 'Nueva Pregunta'}
        size="lg"
      >
        <form onSubmit={handleSavePregunta} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de Pregunta *
              </label>
              <input
                type="number"
                min="1"
                value={preguntaForm.numero_pregunta}
                onChange={(e) => setPreguntaForm({ ...preguntaForm, numero_pregunta: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sección *
              </label>
              <select
                value={preguntaForm.seccion}
                onChange={(e) => setPreguntaForm({
                  ...preguntaForm,
                  seccion: e.target.value,
                  nombre_seccion: NOMBRES_SECCION[e.target.value],
                })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {SECCIONES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Pregunta *
            </label>
            <select
              value={preguntaForm.tipo_pregunta}
              onChange={(e) => setPreguntaForm({ ...preguntaForm, tipo_pregunta: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {TIPOS_PREGUNTA.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enunciado *
            </label>
            <textarea
              value={preguntaForm.enunciado}
              onChange={(e) => setPreguntaForm({ ...preguntaForm, enunciado: e.target.value })}
              required
              rows={3}
              placeholder="Escriba el enunciado de la pregunta..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setShowPreguntaModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" icon={Save}>
              {editingPregunta ? 'Actualizar' : 'Crear'} Pregunta
            </Button>
          </div>
        </form>
      </Modal>

      {/* ===== MODAL: Gestionar Opciones ===== */}
      <Modal
        isOpen={showOpcionesModal}
        onClose={() => { setShowOpcionesModal(false); setEditingOpcion(null); }}
        title={`Opciones - Pregunta ${selectedPregunta?.numero_pregunta}`}
        size="lg"
      >
        {selectedPregunta && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-700">{selectedPregunta.enunciado}</p>
            </div>

            {/* Lista de opciones existentes */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Opciones actuales:</h4>
              {(!selectedPregunta.opciones || selectedPregunta.opciones.length === 0) ? (
                <p className="text-sm text-gray-400 italic">No hay opciones configuradas</p>
              ) : (
                <div className="space-y-2">
                  {selectedPregunta.opciones.map(op => (
                    <div
                      key={op.id_opcion}
                      className="flex items-center justify-between p-3 bg-white border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-primary-100 text-primary-700 text-sm font-bold">
                          {op.codigo_opcion}
                        </span>
                        <div>
                          <span className="text-sm text-gray-900">{op.texto_opcion}</span>
                          <span className="text-xs text-gray-400 ml-2">
                            (Orden: {op.orden} | Valor: {op.valor_ponderado ?? 0})
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditOpcion(op)}
                          className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteOpcion(op)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Formulario agregar/editar opción */}
            <form onSubmit={handleSaveOpcion} className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {editingOpcion ? 'Editar Opción' : 'Agregar Nueva Opción'}
              </h4>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Código *</label>
                  <input
                    type="text"
                    value={opcionForm.codigo_opcion}
                    onChange={(e) => setOpcionForm({ ...opcionForm, codigo_opcion: e.target.value })}
                    required
                    maxLength={10}
                    placeholder="A"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Texto *</label>
                  <input
                    type="text"
                    value={opcionForm.texto_opcion}
                    onChange={(e) => setOpcionForm({ ...opcionForm, texto_opcion: e.target.value })}
                    required
                    placeholder="Sí"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Valor</label>
                    <input
                      type="number"
                      value={opcionForm.valor_ponderado}
                      onChange={(e) => setOpcionForm({ ...opcionForm, valor_ponderado: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Orden</label>
                    <input
                      type="number"
                      min="1"
                      value={opcionForm.orden}
                      onChange={(e) => setOpcionForm({ ...opcionForm, orden: e.target.value })}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button type="submit" variant="primary" size="sm" icon={Save}>
                  {editingOpcion ? 'Actualizar' : 'Agregar'}
                </Button>
                {editingOpcion && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingOpcion(null);
                      setOpcionForm({ ...emptyOpcion, orden: (selectedPregunta.opciones?.length || 0) + 1 });
                    }}
                  >
                    Cancelar edición
                  </Button>
                )}
              </div>
            </form>
          </div>
        )}
      </Modal>

      {/* ===== MODAL: Confirmar Eliminación ===== */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        title="Confirmar Eliminación"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {deleteTarget?.type === 'pregunta' ? (
              <>
                ¿Está seguro de eliminar la <strong>Pregunta {deleteTarget.item.numero_pregunta}</strong>?
                Esto eliminará también todas sus opciones de respuesta.
              </>
            ) : (
              <>
                ¿Está seguro de eliminar la opción <strong>{deleteTarget?.item?.codigo_opcion}</strong> ({deleteTarget?.item?.texto_opcion})?
              </>
            )}
          </p>
          <p className="text-xs text-red-600">Esta acción no se puede deshacer.</p>
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
            >
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmDelete} icon={Trash2}>
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PlantillaEncuestaPage;
